import { useRef, useEffect, useState } from 'react';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { withTheme } from '@mui/styles';
import { useTranslation } from 'next-i18next';
import Carousel from 'react-multi-carousel';
import videojs from 'video.js';

import Image from 'src/components/atoms/image';
import config from 'src/config';
import { useWindowSize } from 'src/helpers/use';

import 'react-multi-carousel/lib/styles.css';
import 'video.js/dist/video-js.css';
import TestimonialStyle from './style';

const INIT_CAROUSEL_STATE = {
  slidesToSlide: 3,
  partialVisible: false,
};

const TESTIMONIAL_COUNT = 3;

const CustomDot = ({ active, onClick }: any) => (
  <Box className={`testimonial-custom-dot ${active ? 'active' : ''}`} onClick={onClick} />
);

const CustomLeftArrow = ({ onClick }: any) => (
  <Box className="previous-button" onClick={onClick}>
    <KeyboardArrowLeftIcon />
  </Box>
);
const CustomRightArrow = ({ onClick }: any) => (
  <Box className="next-button" onClick={onClick}>
    <KeyboardArrowRightIcon />
  </Box>
);

const TestimonialSection = ({ theme }: { theme: any }) => {
  const { t } = useTranslation('business');
  const [carouselState, setCarouselState] = useState(INIT_CAROUSEL_STATE);
  const { width = 0 } = useWindowSize();
  const videoRef = useRef(null);

  const getImage = (index: number, size: string, fileType: string) =>
    `${config.IMAGE_CDN_HOST}/assets/b2b-landing/client-logos/06-logo-${String(index).padStart(
      2,
      '0',
    )}${size}.${fileType}`;

  useEffect(() => {
    if (width >= theme.breakpoints.values.lg) {
      setCarouselState(INIT_CAROUSEL_STATE);
    } else if (width > theme.breakpoints.values.sm && width < theme.breakpoints.values.lg) {
      setCarouselState(INIT_CAROUSEL_STATE);
    } else if (width <= theme.breakpoints.values.sm) {
      setCarouselState({
        slidesToSlide: 1,
        partialVisible: true,
      });
    }
  }, [width]);

  useEffect(() => {
    const videoElement = videoRef.current;
    const options = {
      autoplay: false,
      playsinline: true,
      controls: true,
      responsive: true,
      fluid: true,
      controlBar: {
        volumePanel: { inline: false },
      },
      sources: [
        {
          src: `${config.IMAGE_CDN_HOST}/assets/b2b-landing/testimonials/pmat-onward-video.mp4`,
          type: 'video/mp4',
        },
      ],
    };
    let player: any;
    if (videoElement) {
      player = videojs(videoElement, options);
    }
    return () => {
      if (player) {
        player.dispose();
      }
    };
  }, []);

  return (
    <TestimonialStyle width={width}>
      <div className="container">
        <Typography className="title-eng">{t('testimonials_subtitle')}</Typography>
        <Typography className="title">{t('testimonials_title')}</Typography>
        <Typography className="subtitle">{t('testimonials_description')}</Typography>
        <Typography className="subtitle">{t('testimonials_description_more')}</Typography>
        <Grid container alignItems="center">
          <Grid item lg={6} xs={12}>
            <div className="carousel-container">
              <Carousel
                customDot={<CustomDot />}
                dotListClass="testimonial-dot-list"
                draggable
                focusOnSelect={false}
                customLeftArrow={<CustomLeftArrow />}
                customRightArrow={<CustomRightArrow />}
                removeArrowOnDeviceType={['desktop']}
                renderDotsOutside
                keyBoardControl
                partialVisible={carouselState.partialVisible}
                minimumTouchDrag={80}
                renderButtonGroupOutside={false}
                responsive={{
                  largeDesktop: {
                    breakpoint: { max: 6000, min: theme.breakpoints.values.xl },
                    items: 3,
                  },
                  desktop: {
                    breakpoint: { max: theme.breakpoints.values.xl, min: theme.breakpoints.values.lg },
                    items: 3,
                  },
                  tablet: {
                    breakpoint: { max: theme.breakpoints.values.lg, min: theme.breakpoints.values.sm },
                    items: 3,
                  },
                  mobile: {
                    breakpoint: { max: theme.breakpoints.values.sm, min: 0 },
                    items: 1,
                    partialVisibilityGutter: width * 0.2,
                  },
                }}
                showDots
                slidesToSlide={carouselState.slidesToSlide}
                swipeable
              >
                {Array.from({ length: TESTIMONIAL_COUNT }, (_, i) => i + 1).map((index) => (
                  <div className="testimonial-card" key={index}>
                    <div
                      className="image"
                      style={{
                        // eslint-disable-next-line max-len
                        backgroundImage: `linear-gradient(to top, #333, rgba(63, 63, 63, 0.26) 34%, rgba(85, 85, 85, 0) 50%), url('${config.IMAGE_CDN_HOST}/assets/b2b-landing/testimonials/user${index}.png')`,
                      }}
                    >
                      <Typography className="content">{t(`${index}_user_comment`)}</Typography>
                      <Typography className="user-info">- {t(`${index}_user_full_name`)} -</Typography>
                      <Typography className="user-info">{t(`${index}_user_position`)}</Typography>
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </Grid>
          <Grid item lg={6} xs={12} display="flex" justifyContent="center" className="video-container">
            <div data-vjs-player>
              <video ref={videoRef} className="video-js vjs-big-play-centered testimonial-video" />
            </div>
          </Grid>
        </Grid>
      </div>
      <Carousel
        autoPlay
        infinite
        draggable={false}
        swipeable
        arrows={false}
        autoPlaySpeed={1000}
        centerMode={false}
        className="client-logos-container"
        focusOnSelect={false}
        renderDotsOutside={false}
        itemClass="logo-item"
        showDots={false}
        sliderClass=""
        slidesToSlide={1}
        responsive={{
          largeDesktop: {
            breakpoint: { max: 6000, min: theme.breakpoints.values.xl },
            items: 20,
          },
          desktop: {
            breakpoint: { max: theme.breakpoints.values.xl, min: theme.breakpoints.values.lg },
            items: 10,
          },
          tablet: {
            breakpoint: { max: theme.breakpoints.values.lg, min: theme.breakpoints.values.sm },
            items: 6,
          },
          mobile: {
            breakpoint: { max: theme.breakpoints.values.sm, min: 0 },
            items: 3,
          },
        }}
      >
        {Array.from({ length: 44 }, (_, i) => i + 1).map((index: number) => (
          <Image
            style={{ width: 80, height: 40 }}
            key={index}
            alt={t('organization_choose_skilllane')}
            src={getImage(index, '', 'png')}
            srcSet={`${getImage(index, '', 'png')} 1x, ${getImage(index, '@2', 'png')} 2x`}
            srcSetWebp={`${getImage(index, '', 'webp')} 1x, ${getImage(index, '@2', 'webp')} 2x`}
          />
        ))}
      </Carousel>
    </TestimonialStyle>
  );
};

export default withTheme(TestimonialSection);
